/**
 * This script fixes subscription spilling on the Bold widget when both Bold and Stay selling plans are active. It hides the Stay.ai plan group named Monthly Subscription Kit*.
 * Monitors: initial load (poll), bfcache (pageshow), DOM replacement (MutationObserver).
 *
 * If the group name does not match Bold’s `sellingPlanGroups[].name`, the “no matching group name”
 */
(function () {
  const LOG_PREFIX = '>>> [stayai-fixBoldSubsApp]';
  const GROUP_NAME_TO_HIDE = 'Monthly Subscription Kit*';
  const POLL_MS = 50;
  const POLL_MAX_TRIES = 100;
  const MO_DEBOUNCE_MS = 150;

  /** Set once if lookup by name fails — avoids spam when MutationObserver re-runs on constant DOM churn. */
  let loggedNoMatchingGroupName = false;

  function isBoldWidgetReady() {
    const bsub = window.BOLD?.BsubWidget;
    const groups = bsub?.sellingPlanGroups;
    if (!bsub || !groups || Object.keys(groups).length === 0) return false;
    return !!document.querySelector('[data-bsub-widget]');
  }

  let applyRaf = null;
  function scheduleApplyHide(source) {
    if (applyRaf != null) return;
    applyRaf = requestAnimationFrame(function () {
      applyRaf = null;
      if (!isBoldWidgetReady()) return;
      hideBoldGroupByName(source);
    });
  }

  let moTimer = null;
  function scheduleApplyHideDebounced() {
    if (moTimer != null) clearTimeout(moTimer);
    moTimer = setTimeout(function () {
      moTimer = null;
      // Debounce: themes can fire many mutations; we only log when hideBoldGroupByName runs (see "applied").
      scheduleApplyHide('mutation');
    }, MO_DEBOUNCE_MS);
  }

  function isElementHidden(el) {
    if (!el) return true;
    if (el.style && el.style.display === 'none') return true;
    return getComputedStyle(el).display === 'none';
  }

  /** True when our hide is already in effect — avoids log spam when Bold mutates the widget and MO re-fires. */
  function isTargetGroupAlreadyHidden(groupInput, groupBlock, optionsBlock) {
    if (!groupInput || !groupBlock) return false;
    if (!groupInput.disabled) return false;
    if (groupInput.checked) return false;
    if (!isElementHidden(groupBlock)) return false;
    if (optionsBlock && !isElementHidden(optionsBlock)) return false;
    return true;
  }

  function hideBoldGroupByName(source) {
    const widget = document.querySelector('[data-bsub-widget]');
    const bsub = window.BOLD?.BsubWidget;
    if (!widget || !bsub?.sellingPlanGroups) {
      console.log(LOG_PREFIX, 'hideBoldGroupByName: skip (no widget or sellingPlanGroups)', {
        source: source,
      });
      return;
    }

    const wanted = GROUP_NAME_TO_HIDE.trim();
    const match = Object.entries(bsub.sellingPlanGroups).find(([, group]) => {
      return group && typeof group.name === 'string' && group.name.trim() === wanted;
    });
    if (!match) {
      if (!loggedNoMatchingGroupName) {
        loggedNoMatchingGroupName = true;
        const availableNames = Object.values(bsub.sellingPlanGroups)
          .map(function (g) {
            return g && g.name;
          })
          .filter(Boolean);
        console.log(LOG_PREFIX, 'hideBoldGroupByName: no matching group name (logged once per page)', {
          source: source,
          wanted: GROUP_NAME_TO_HIDE,
          availableNames: availableNames
        });
      }
      return;
    }

    const [groupId] = match;

    const groupInput = widget.querySelector(
      `[data-bsub-selling-plan-group-input][value="${groupId}"]`
    );
    const groupBlock = groupInput?.closest('[data-bsub-selling-plan-group]');
    const optionsBlock = widget.querySelector(
      `.bsub-options-container[data-bsub-selling-plan-group-id="${groupId}"]`
    );

    if (!groupInput) return;

    if (isTargetGroupAlreadyHidden(groupInput, groupBlock, optionsBlock)) {
      return;
    }

    if (groupBlock) groupBlock.style.display = 'none';
    if (optionsBlock) optionsBlock.style.display = 'none';
    if (groupInput) groupInput.disabled = true;

    let fallbackUsed = false;
    if (groupInput?.checked) {
      const fallback = Array.from(
        widget.querySelectorAll('[data-bsub-selling-plan-group-input]')
      ).find(function (input) {
        return input.value !== groupId && !input.disabled;
      });

      if (fallback) {
        fallback.checked = true;
        fallback.dispatchEvent(new Event('change', { bubbles: true }));
        fallbackUsed = true;
      } else {
        console.log(LOG_PREFIX, 'hideBoldGroupByName: hidden group was checked but no fallback radio', {
          source: source,
          groupId: groupId,
        });
      }
    }

    console.log(LOG_PREFIX, 'hideBoldGroupByName: applied', {
      source: source,
      groupId: groupId,
      groupName: GROUP_NAME_TO_HIDE,
      fallbackUsed: fallbackUsed,
    });
  }

  function waitForBoldThenHide() {
    let tries = 0;
    const timer = setInterval(function () {
      tries += 1;

      if (isBoldWidgetReady()) {
        clearInterval(timer);
        console.log(LOG_PREFIX, 'poll: Bold widget ready', { tries: tries, maxTries: POLL_MAX_TRIES });
        hideBoldGroupByName('poll');
        return;
      }

      if (tries > POLL_MAX_TRIES) {
        clearInterval(timer);
        console.log(LOG_PREFIX, 'poll: stopped (max tries — Bold widget or groups never became ready)', {
          maxTries: POLL_MAX_TRIES,
          intervalMs: POLL_MS,
        });
      }
    }, POLL_MS);
  }

  function bindLifecycle() {
    window.addEventListener('pageshow', function () {
      // console.log(LOG_PREFIX, 'pageshow → re-apply hide (e.g. back/forward cache)');
      scheduleApplyHide('pageshow');
    });

    if (typeof MutationObserver === 'undefined') return;

    const mo = new MutationObserver(function () {
      scheduleApplyHideDebounced();
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
    // console.log(LOG_PREFIX, 'MutationObserver: watching document for widget DOM changes');
  }

  function init() {
    waitForBoldThenHide();
    bindLifecycle();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
